import {find, last, startCase} from 'lodash';
import {HoistModel, LoadSpec, persist, XH} from '@xh/hoist/core';
import {action, bindable, computed, observable, observableRef, runInAction} from '@xh/hoist/mobx';
import {wait} from '@xh/hoist/promise';
import {createObservableRef} from '@xh/hoist/utils/react';
import {
    AiChatEffort,
    AiChatErrorCode,
    AiChatFailure,
    AiChatModelDefinition,
    AiChatOptions,
    AiChatResponse,
    AiChatStreamEvent,
    AiChatTurn,
    AiChatUserUsage
} from '../../data/AiChatTypes';

export type AiChatExchangeStatus = 'pending' | 'streaming' | 'completed' | 'failed' | 'cancelled';

export interface AiChatToolProgress {
    tool: string;
    label: string;
    done: boolean;
    isError: boolean;
    durationMs: number;
}

export interface AiChatStreamRound {
    round: number;
    text: string;
    tools: AiChatToolProgress[];
}

/** One question and its (streaming, completed or failed) answer. */
export class AiChatExchange {
    /** Sent as the request id, so progress events find their way back here. */
    readonly id: string = XH.genId();
    readonly question: string;

    @observable accessor status: AiChatExchangeStatus = 'pending';
    @observable accessor rounds: AiChatStreamRound[] = [];
    @observableRef accessor response: AiChatResponse = null;
    @observable accessor errorCode: AiChatErrorCode = null;
    @observable accessor error: string = null;
    @observable accessor showDetails = false;

    constructor(question: string) {
        this.question = question;
    }

    @action
    toggleDetails() {
        this.showDetails = !this.showDetails;
    }

    get isActive(): boolean {
        return this.status === 'pending' || this.status === 'streaming';
    }

    get isFailed(): boolean {
        return this.status === 'failed' || this.status === 'cancelled';
    }

    /** Changes whenever streamed content grows - drives auto-scroll. */
    @computed
    get contentVersion(): string {
        const textLen = this.rounds.reduce((sum, r) => sum + r.text.length + r.tools.length, 0);
        return `${this.status}:${this.rounds.length}:${textLen}`;
    }
}

/**
 * State of the app-wide AI chat side panel: visibility, the conversation (kept client side and
 * resent on every question), the in-flight question, and the model / effort choice.
 */
export class AiChatModel extends HoistModel {
    override persistWith = {localStorageKey: 'rzxAiChat'};

    @bindable @persist accessor isOpen = false;
    @bindable @persist accessor modelId: string = null;
    @bindable @persist accessor effort: AiChatEffort = null;
    @bindable accessor inputText = '';

    @observableRef accessor options: AiChatOptions = null;
    @observableRef accessor usage: AiChatUserUsage = null;
    @observableRef accessor exchanges: AiChatExchange[] = [];
    @observable accessor loadError: string = null;

    /** Sent with each question - a new one for each new conversation. */
    private conversationId: string = crypto.randomUUID();

    scrollRef = createObservableRef<HTMLDivElement>();

    constructor() {
        super();

        XH.messageHub.subscribe('ai_chat', this.xhId, message => this.onProgress(message.payload));

        this.addReaction(
            {
                track: () => this.isOpen,
                run: isOpen => {
                    if (isOpen && !this.options) this.loadOptionsAsync();
                },
                fireImmediately: true
            },
            {
                track: () => [this.exchanges.length, last(this.exchanges)?.contentVersion],
                run: () => this.scrollToBottomAsync()
            }
        );
    }

    override async doLoadAsync(loadSpec: LoadSpec) {
        const {aiChatService} = XH,
            [options, usage] = await Promise.all([aiChatService.getOptionsAsync(), aiChatService.getUsageAsync()]);

        runInAction(() => {
            this.options = options;
            this.usage = usage;
            // Drop a persisted choice the backend no longer offers
            if (this.modelId && !find(options.models, {id: this.modelId})) this.modelId = null;
            if (this.effort && !options.efforts.includes(this.effort)) this.effort = null;
        });
    }

    /** Load (or retry loading) the options and usage, recording any failure for display in the panel. */
    async loadOptionsAsync() {
        runInAction(() => (this.loadError = null));
        try {
            await this.loadAsync();
        } catch (e) {
            XH.handleException(e, {showAlert: false});
            runInAction(() => (this.loadError = e.message ?? 'The AI chat service is unavailable.'));
        }
    }

    override destroy() {
        this.cancel();
        XH.messageHub.unSubscribe('ai_chat', this.xhId);
        super.destroy();
    }

    //------------------
    // Derived state
    //------------------
    get isBusy(): boolean {
        return !!last(this.exchanges)?.isActive;
    }

    get canSend(): boolean {
        return !this.isBusy && !!this.inputText.trim();
    }

    get activeModelDef(): AiChatModelDefinition {
        const {options} = this;
        return options ? find(options.models, {id: this.modelId ?? options.defaultModel}) : null;
    }

    get effortEnabled(): boolean {
        return !!this.activeModelDef?.supportsEffort;
    }

    //------------------
    // Actions
    //------------------
    @action
    toggleOpen() {
        this.isOpen = !this.isOpen;
    }

    @action
    close() {
        this.isOpen = false;
    }

    @action
    newConversation() {
        this.cancel();
        this.exchanges = [];
        this.inputText = '';
        this.conversationId = crypto.randomUUID();
    }

    @action
    cancel() {
        const exchange = last(this.exchanges);
        if (!exchange?.isActive) return;
        XH.aiChatService.cancel(exchange.id);
        this.fail(exchange, {errorCode: 'cancelled', error: null});
    }

    /** Ask the question in the input box. */
    async sendAsync() {
        if (!this.canSend) return;
        const question = this.inputText.trim();
        runInAction(() => (this.inputText = ''));
        await this.askAsync(question);
    }

    /** Ask one of the suggested questions shown on an empty conversation. */
    async askExampleAsync(question: string) {
        if (this.isBusy) return;
        await this.askAsync(question);
    }

    /** Resend a failed / cancelled question as is. */
    async retryAsync(exchange: AiChatExchange) {
        if (this.isBusy) return;
        await this.askAsync(exchange.question);
    }

    /** Move a failed / cancelled question back to the input box for editing. */
    @action
    edit(exchange: AiChatExchange) {
        if (this.isBusy) return;
        this.exchanges = this.exchanges.filter(it => it !== exchange);
        this.inputText = exchange.question;
    }

    //------------------
    // Implementation
    //------------------
    private async askAsync(question: string) {
        const exchange = new AiChatExchange(question);

        // Failed questions are never part of the history (it must alternate user / assistant),
        // so drop them from the transcript once a new question is asked.
        runInAction(() => (this.exchanges = [...this.exchanges.filter(it => !it.isFailed), exchange]));

        const messages: AiChatTurn[] = [];
        this.exchanges.forEach(it => {
            messages.push({role: 'user', text: it.question});
            if (it.response) messages.push({role: 'assistant', text: it.response.answer});
        });

        const {modelId, effort, effortEnabled} = this;

        try {
            const response = await XH.aiChatService.chatAsync({
                messages,
                requestId: exchange.id,
                conversationId: this.conversationId,
                model: modelId ?? undefined,
                effort: effort && effortEnabled ? effort : undefined
            });
            this.complete(exchange, response);
        } catch (e) {
            this.fail(exchange, XH.aiChatService.toFailure(e));
        }
    }

    /** Progress of a running question, pushed over MessageHub while the request is in flight. */
    @action
    private onProgress(event: AiChatStreamEvent) {
        // No payload on a RELOAD message. No active exchange for another tab's question, or for
        // stragglers after the answer, a cancel or a new conversation.
        const exchange = event ? this.exchanges.find(it => it.id === event.requestId) : null;
        if (!exchange?.isActive) return;

        switch (event.type) {
            case 'Started':
                exchange.status = 'streaming';
                break;
            case 'RoundStarted':
                exchange.rounds.push({round: event.round, text: '', tools: []});
                break;
            case 'TextDelta':
                this.getRound(exchange, event.round).text += event.text ?? '';
                break;
            case 'ToolStarted':
                this.getRound(exchange, event.round).tools.push({
                    tool: event.tool,
                    label: toolLabel(event.tool, event.arguments),
                    done: false,
                    isError: false,
                    durationMs: null
                });
                break;
            case 'ToolFinished': {
                const toolProgress = this.getRound(exchange, event.round).tools.find(t => t.tool === event.tool && !t.done);
                if (toolProgress) {
                    toolProgress.done = true;
                    toolProgress.isError = !!event.isError;
                    toolProgress.durationMs = event.durationMs;
                }
                break;
            }
        }
    }

    @action
    private complete(exchange: AiChatExchange, response: AiChatResponse) {
        // Cancelled, or the conversation was cleared, while the answer was on its way
        if (!exchange.isActive) return;
        exchange.response = response;
        exchange.status = 'completed';
        this.refreshUsageAsync();
    }

    @action
    private fail(exchange: AiChatExchange, {errorCode, error, usage}: AiChatFailure) {
        if (!exchange.isActive) return;
        if (usage) this.usage = usage;
        exchange.status = errorCode === 'cancelled' ? 'cancelled' : 'failed';
        exchange.errorCode = errorCode;
        exchange.error = error;
    }

    private getRound(exchange: AiChatExchange, round: number): AiChatStreamRound {
        let ret = exchange.rounds.find(r => r.round === round);
        if (!ret) {
            ret = {round, text: '', tools: []};
            exchange.rounds.push(ret);
        }
        return ret;
    }

    private async refreshUsageAsync() {
        try {
            const usage = await XH.aiChatService.getUsageAsync();
            runInAction(() => (this.usage = usage));
        } catch (e) {
            XH.handleException(e, {showAlert: false});
        }
    }

    private async scrollToBottomAsync() {
        await wait();
        const el = this.scrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }
}

//------------------
// Tool progress labels
//------------------
const TOOL_LABELS: Record<string, string> = {
    search_catalog: 'Searching the data catalog',
    portfolio_summary: 'Summarizing portfolios',
    performance_returns: 'Computing performance returns',
    sector_exposure: 'Computing sector exposure',
    deal_performance: 'Computing deal performance'
};

function toolLabel(tool: string, args: Record<string, unknown>): string {
    const dataset = typeof args?.dataset === 'string' ? args.dataset.replace(/_/g, ' ') : null;
    if (tool === 'query_dataset' && dataset) return `Querying ${dataset}`;
    if (tool === 'describe_dataset' && dataset) return `Reading about ${dataset}`;
    return TOOL_LABELS[tool] ?? startCase(tool);
}
